import * as React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { translate } from 'secullum-i18n';
import {
  Card,
  ErrorMessage,
  isTablet,
  Message,
  Space,
  TextBox
} from 'secullum-react-native-ui';
import { ButtonBar } from '../../components/ButtonBar';
import Api from '../../modules/api';
import { getTheme } from '../../modules/layout';
import { DadosFuncionario } from '../../modules/types';

interface Props {
  dados: DadosFuncionario;
  onConcluir: () => void;
  onCancelar: () => void;
  onErroConexao?: (mensagem: string, onTentarNovamente: () => void) => void;
}

interface State {
  diasVenda: string;
  observacoes: string;
  erros: { [key: string]: string };
  resultado: 'sucesso' | 'erro' | null;
  erroGeral: string;
  bloquearEnviar: boolean;
}

class CardVenderFerias extends React.Component<Props, State> {
  state: State = {
    diasVenda: '',
    observacoes: '',
    erros: {},
    resultado: null,
    erroGeral: '',
    bloquearEnviar: false
  };

  consistirDados = (dias: number): { [key: string]: string } | null => {
    if (!dias || dias <= 0) {
      return {
        feriasVendaDias: translate(
          'O campo {0} deve ser maior que zero.',
          translate('Quantidade')
        )
      };
    }

    return null;
  };

  handleEnviar = () => {
    const { diasVenda, observacoes, bloquearEnviar } = this.state;
    const api = new Api();
    const dias = parseInt(diasVenda, 10);

    if (bloquearEnviar) {
      return;
    }

    const erros = this.consistirDados(dias);

    if (erros) {
      this.setState({ erros });
      return;
    }

    this.setState({ bloquearEnviar: true });

    api.postSolicitacaoVendaFerias(
      {
        funcionarioId: this.props.dados.id,
        feriasVendaDias: dias,
        observacoes
      },
      {
        onSuccess: () => {
          this.setState({ resultado: 'sucesso', bloquearEnviar: false });
        },
        onError: err => {
          this.setState({ bloquearEnviar: false });

          if (err.name === 'TimeoutError' && this.props.onErroConexao) {
            this.props.onErroConexao(err.message, this.handleEnviar);
          } else {
            this.setState({ resultado: 'erro', erroGeral: err.message });
          }
        },
        onValidationError: (_, errorObj) => {
          this.setState({ erros: errorObj, bloquearEnviar: false });
        }
      }
    );
  };

  render() {
    const {
      diasVenda,
      observacoes,
      erros,
      resultado,
      erroGeral,
      bloquearEnviar
    } = this.state;
    const plataformaWeb = Platform.OS === 'web';

    return (
      <>
        <Card>
          <Card.Header
            nativeID="ferias-vender-texto-explicativo"
            title={translate(
              'A venda de férias deve ser utilizada para solicitar a conversão de parte das férias em abono pecuniário.'
            )}
            titleStyle={styles.titleHeader}
          />
          <Card.Section>
            <View style={plataformaWeb && styles.centralizeView}>
              <TextBox
                nativeID="ferias-vender-quantidade"
                label={translate('Quantidade')}
                value={diasVenda}
                keyboardType="numeric"
                maxLength={3}
                onChange={(diasVenda: string) =>
                  this.setState({
                    diasVenda: diasVenda.replace(/[^0-9]/g, '')
                  })
                }
              />
              <ErrorMessage
                nativeID="ferias-vender-erro-quantidade"
                message={erros.feriasVendaDias}
              />
              <Space />
              <TextBox
                nativeID="ferias-vender-motivo"
                label={translate('Motivo')}
                value={observacoes}
                multiline={true}
                maxLength={100}
                inputStyle={
                  plataformaWeb ? { minHeight: 60 } : { maxHeight: 60 }
                }
                onChange={observacoes => this.setState({ observacoes })}
              />
              <Space />
              <ButtonBar
                buttonBarStyle={plataformaWeb && styles.centralizeView}
                leftButton={{
                  nativeID: 'ferias-vender-cancelar',
                  text: translate('Cancelar'),
                  primary: false,
                  onPress: this.props.onCancelar
                }}
                rightButton={{
                  nativeID: 'ferias-vender-enviar',
                  text: translate('Enviar'),
                  disabled: bloquearEnviar,
                  onPress: this.handleEnviar
                }}
              />
            </View>
          </Card.Section>
        </Card>
        <Message
          nativeID="ferias-vender-sucesso"
          message={translate('Solicitação enviada com sucesso')}
          visible={resultado === 'sucesso'}
          onRequestClose={this.props.onConcluir}
        />
        <Message
          nativeID="ferias-vender-erro"
          type="warning"
          message={erroGeral}
          visible={resultado === 'erro'}
          onRequestClose={() =>
            this.setState({ resultado: null, erroGeral: '' })
          }
        />
      </>
    );
  }
}

const theme = getTheme();

const styles = StyleSheet.create({
  centralizeView: {
    margin: 'auto',
    maxWidth: 350,
    width: '100%'
  },
  titleHeader: {
    color: theme.textColor2,
    fontSize: isTablet() ? 16 : 12,
    fontFamily: theme.fontFamily2
  }
});

export default CardVenderFerias;
